/* Quaza Track's data layer on the website: every price the browser reads comes from here.

   The source is quaza-prices-data, the static publication of the prices (rebuilt after each daily
   ingest by the publisher in the quaza-prices repo). Static files are free and unlimited to serve, so
   nothing a visitor does on the website reaches the quaza-prices Worker or its D1 database, which the
   iOS app depends on and which stops answering everyone past 5M row reads a day.

   Most pages need none of this: Liquid prints today's figures from the store data the hub's daily
   track_sync writes. This is for what a visitor asks for after the page loads: a chart, a range, a
   search, "show all".

   Files (see publish/README.md in the quaza-prices repo):
     legacy/catalog.json                curated catalog, same bytes as the Worker's GET /catalog
     legacy/indexes/<range>.msrp.json   GET /indexes
     legacy/set/<key>/<range>.json      GET /indexes/set/<name>; <key> is setKey(name) below
     v2/p/<id>.json                     a curated product's full series [date, cad_cents, usd_cents]
     v2/history/<id % 512>.json         any tracked product: daily USD series, facts, printings
     v2/sets.json, v2/sets/<id>.json    every set, and one set's products
     v2/search/en.json, jp.json         compact search rows (fields named in `fields`)

   Every fetch is memoised per page view; the files revalidate with ETags (max-age=0). */
(function () {
  if (window.QuazaTrackData) return;
  var BASE = 'https://quaza-prices-data.quazacollect.workers.dev';
  var RANGES = { '1w': 8, '1m': 30, '3m': 91, '6m': 183, '12m': 365, max: 3650 };
  var memo = {};

  function json(path) {
    if (!memo[path]) {
      memo[path] = fetch(BASE + '/' + path, { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (r.status === 404) return null;
          if (!r.ok) throw new Error(path + ' ' + r.status);
          return r.json();
        })
        .catch(function (e) { delete memo[path]; throw e; });
    }
    return memo[path];
  }

  function rangeKey(r) { return Object.prototype.hasOwnProperty.call(RANGES, r) ? r : '3m'; }
  function isoDay(ms) { return new Date(ms).toISOString().slice(0, 10); }

  /* publish/build.mjs setKey(): readable slug plus the first 8 hex of the name's SHA-1. */
  function setKey(name) {
    var slug = String(name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
    var bytes = new TextEncoder().encode(String(name));
    return crypto.subtle.digest('SHA-1', bytes).then(function (buf) {
      var hex = Array.prototype.map.call(new Uint8Array(buf), function (b) {
        return ('0' + b.toString(16)).slice(-2);
      }).join('');
      return (slug || 'set') + '-' + hex.slice(0, 8);
    });
  }

  /* The answer GET /prices/:id gave, sliced from the product's full series the way the Worker's
     published.js does: same keys, same maths, so the tracker's views need no change. */
  function detail(base, series, range, rates) {
    var start = isoDay(Date.now() - RANGES[rangeKey(range)] * 86400e3);
    var pts = series.filter(function (s) { return s[0] >= start; });
    var first = pts[0], last = pts[pts.length - 1];
    var abs = first && last ? last[1] - first[1] : null;
    var vals = pts.map(function (s) { return s[1]; });
    var out = {};
    for (var k in base) out[k] = base[k];
    out.currency = 'CAD';
    out.rates = rates || null;
    out.current_cents = last ? last[1] : null;
    out.change_abs_cents = abs;
    out.change_pct = first && first[1] > 0 && abs != null ? Math.round((abs / first[1]) * 1000) / 10 : null;
    out.high_cents = vals.length ? Math.max.apply(null, vals) : null;
    out.low_cents = vals.length ? Math.min.apply(null, vals) : null;
    out.series = pts;
    return out;
  }

  var api = {
    base: BASE,
    setKey: setKey,
    catalog: function () { return json('legacy/catalog.json'); },
    indexes: function (range) { return json('legacy/indexes/' + rangeKey(range) + '.msrp.json'); },
    setIndex: function (name, range) {
      return setKey(name).then(function (k) { return json('legacy/set/' + k + '/' + rangeKey(range) + '.json'); });
    },
    sets: function () { return json('v2/sets.json'); },
    set: function (id) { return json('v2/sets/' + Number(id) + '.json'); },
    search: function (lang) { return json('v2/search/' + (lang === 'jp' ? 'jp' : 'en') + '.json'); },
    movers: function () { return json('v2/movers.json'); },

    /* A curated product, as GET /prices/:id answered: CAD cents. */
    product: function (id, range) {
      return Promise.all([json('v2/p/' + encodeURIComponent(id) + '.json'), api.catalog()]).then(function (r) {
        var p = r[0];
        if (!p) return { error: 'unknown_product' };
        var base = { id: p.id, name: p.name, set_name: p.set_name, era: p.era, type: p.type, language: p.language,
                     image_url: p.image_url, shopify_handle: p.shopify_handle };
        var series = p.series.map(function (s) { return [s[0], s[1]]; });
        return detail(base, series, range, r[1] && r[1].rates);
      });
    },

    /* Any tracked product's history entry, by Track id, or null. */
    history: function (tcgId) {
      var id = Number(tcgId);
      return json('v2/history/' + (id % 512) + '.json').then(function (b) {
        return (b && b.items && b.items[id]) || null;
      });
    },

    /* A history entry as a /prices/:id-shaped detail in CAD cents (the chart widget's unit), for one
       printing when the entry carries it. usdToCad comes from the catalog's rates. */
    historyDetail: function (entry, range, usdToCad, printing, base) {
      var src = (printing && entry.printings && entry.printings[printing]) || entry;
      var t0 = Date.parse(src.start + 'T00:00:00Z');
      var series = [];
      for (var i = 0; i < src.usd_cents.length; i++) {
        var c = src.usd_cents[i];
        if (c == null) continue;
        series.push([isoDay(t0 + i * 86400e3), Math.round(c * usdToCad)]);
      }
      return detail(base || {}, series, range, null);
    },

    /* USD -> CAD from the catalog's rates (CAD-based: rates.USD is CAD -> USD). */
    usdToCad: function () {
      return api.catalog().then(function (c) { return c && c.rates && c.rates.USD ? 1 / c.rates.USD : null; });
    },

    /* Our copy of the product's picture in Shopify Files, resized by Shopify (snippets/qt-card-img.liquid
       is the Liquid twin), or null for ids with no picture anywhere (window.QT_IMG_NONE, ",id,id,",
       printed by the page from the shop metafield track.img_none). */
    imageUrl: function (tcgId, size) {
      var id = Number(tcgId);
      if (!id || String(window.QT_IMG_NONE || '').indexOf(',' + id + ',') !== -1) return null;
      // the old 200/400 px files held a card 144/287 px wide: same pixels, our copy
      return '/cdn/shop/files/qt-card-' + id + '.jpg?width=' + ((size || 200) >= 400 ? 287 : 144) + '&quality=75';
    }
  };
  window.QuazaTrackData = api;
})();
